import { SitePro } from '../crm/sites/site.pro';
import { Entity } from '../entity';
import { Job } from '../job/job';
import { OpenSystemWorkOrderStatusValues, SystemWorkOrderStatus } from '../job/system.workorder.status';
import { WorkOrder } from '../job/workorder';
import { Allocation } from '../scheduler/allocation';
import { ActivityLocation } from './activity.location';
import { Address } from './address';
import { PagingOptions } from './paging.options';

export type LocationEntity =
    Partial<ActivityLocation>  // for old resource location
    & {
        _id: string;
        title?: string;
        description?: string;

        count?: number;

        /**
         * **location** is for **site** | **job** | **appointment**
         */
        type?: "location" | "resource" | "company";
        siteIds?: string[];
    };



export enum LocationGroupType {
    State = 'State',
    Suburb = 'Suburb',
    Street = 'Street'
}


export type LocationFilter =
    {
        type: 'site',
        filter: SiteLocationFilter
    }
    | {
        type: "job"
        filter: JobLocationFitter
    }
    | {
        type: "appointment"
        filter: AppointmentLocationFitter
    };


export type LocationDataType = LocationFilter['type'];
export type LocationDataFilter = LocationFilter['filter'];

export type LocationMapUIFilters = {
    customers: Entity[];
    zones: Entity[];

    site: any,
    job: any,
    appointment: any,
}


// #region LOCATIONS QUERY

type LocationQueryBase = {
    groupType: LocationGroupType;
    boundingBoxQuery: {
        bottomLeft: [number, number],
        topRight: [number, number]
    };
}

export type LocationQuery =
    LocationQueryBase
    & {
        groupLocationId?: string;
        filters?: Array<LocationFilter>;
    }

export type ExploreLocationQuery =
    LocationQueryBase
    & {
        filters?: Array<LocationFilter>
        ids: string[];
        paging: PagingOptions;
    }

export type LocationDetailQuery = {
    ids: string[];
    paging: PagingOptions;
    filter: LocationFilter;
}

// #endregion


// #region ENTITY LOCATION 

export type SiteLocation = {
    _id: string;
    name: string;
    address: Address;

    jobs: string[];
    appointments: string[];
    sites: string[];

    siteCount: number;
    siteIds: string[];
}


export type ExploreLocationResponse = {
    items: SiteLocation[];
    paging: PagingOptions;
}

export type LocationDetailResponse<T extends LocationDataType = LocationDataType> =
    {
        paging?: PagingOptions;
        error?: string;
    } & (
        T extends 'site'
        ? { type?: Extract<LocationDataType, 'site'>; items?: SiteLocationEntity[]; }
        : T extends 'appointment'
        ? { type: Extract<LocationDataType, 'appointment'>; items?: AppointmentLocationEntity[]; }
        : T extends 'job'
        ? { type: Extract<LocationDataType, 'job'>; items?: JobLocationEntity[]; }
        : never
    );

export type SiteLocationEntity = Pick<
    SitePro,
    '_id'
    | 'name'
    | 'phone'
    | 'email'
    | 'placeId'
    | 'manager'
    | 'owner'
    | 'contact'
    | 'parent'
    | 'services'
    | 'address'
    | 'status'
    | 'tags'
    | 'schedulingTags'
>

export type AppointmentLocationEntity = Pick<
    Allocation,
    '_id'
    | 'resource'
    | 'startDate'
    | 'endDate'
    | 'job'
    | 'workOrder'
    | 'status'
    | 'note'
    | 'bookingStatus'
    | 'locked'
    | 'allDay'
> & {
    timezone?: string;
}

export type JobLocationEntity = Pick<
    WorkOrder,
    '_id'
    | 'jobId'
    | 'jobNumber'
    | 'createdAt'
    | 'startDate'
    | 'dueDate'
    | 'customer'
    | 'priority'
    | 'jobType'
    | 'status'
    | 'owner'
    | 'technician'
    | 'resources'
    | 'labels'
    | 'workCategory'
    | 'workOrderNumber'
>


// #region

// #region ENTITY LOCATION FILTERS

export type SiteLocationFilter = {
    customers?: string[];
    zones?: string[];

    serviceIncluded?: boolean;
}

export type AppointmentLocationFitter = {
    customers?: string[];

    startDate: Date;
    endDate?: Date;
    workCategories?: string[];
}

export type JobLocationFitter = {
    customers?: string[];
    zones?: string[];

    jobTypes?: string[];
    workCategories?: string[];
    status?: Array<SystemWorkOrderStatus.Open | SystemWorkOrderStatus.InProgress | SystemWorkOrderStatus.OnHold>;
}

// #endregion